import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ChatApiService } from '../../shared-services/chat-api.service';

interface FloatingChatMessage {
  text: string;
  sender: 'user' | 'agent';
  formattedText?: string;
  files?: File[];
}

/**
 * App-wide floating chat launcher, mounted once in the side-navbar shell so it
 * appears on every screen (dashboards, builders, published views) without
 * per-page wiring. Reuses ChatApiService — the same session/backend the
 * routed `/chat` page and in-dashboard chat widget already talk to.
 */
@Component({
  selector: 'app-floating-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './floating-chat-widget.component.html',
  styleUrls: ['./floating-chat-widget.component.scss'],
})
export class FloatingChatWidgetComponent implements OnDestroy {
  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLElement>;
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  isFullscreen = false;
  isLoading = false;
  currentMessage = '';
  selectedFiles: File[] = [];
  private sessionStarted = false;
  private objectUrlMap = new Map<File, string>();

  messages: FloatingChatMessage[] = [
    {
      text: 'Hello! How can I help you today?',
      sender: 'agent',
    },
  ];

  constructor(private chatApiService: ChatApiService) {}

  ngOnDestroy(): void {
    this.objectUrlMap.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrlMap.clear();
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.sessionStarted) {
      this.startSession();
    }
    if (this.isOpen) {
      this.scrollToBottom();
    } else {
      this.isFullscreen = false;
    }
  }

  close(): void {
    this.isOpen = false;
    this.isFullscreen = false;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  private startSession(): void {
    this.sessionStarted = true;
    this.chatApiService.createSession().then((observable) => {
      observable.subscribe({
        next: () => {},
        error: (error) => console.error('Error creating chat session:', error),
      });
    });
  }

  triggerAttach(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const fileList = input.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        this.selectedFiles.push(fileList[i]);
      }
    }
    input.value = '';
  }

  removeFile(file: File): void {
    this.selectedFiles = this.selectedFiles.filter((f) => f !== file);
  }

  isImageFile(file: File): boolean {
    return typeof file?.type === 'string' && file.type.startsWith('image/');
  }

  getFilePreviewUrl(file: File): string | null {
    if (!this.isImageFile(file)) {
      return null;
    }
    const cached = this.objectUrlMap.get(file);
    if (cached) {
      return cached;
    }
    const url = URL.createObjectURL(file);
    this.objectUrlMap.set(file, url);
    return url;
  }

  sendMessageOnEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  private formatText(text: string): string {
    let formatted = this.escapeHtml(text);
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li>$1</li>');
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul>$1</ul>');
    formatted = formatted.replace(/\n/g, '<br>');
    return formatted;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  async sendMessage(): Promise<void> {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }

    const userMessage: FloatingChatMessage = {
      text: this.currentMessage,
      sender: 'user',
      files: [...this.selectedFiles],
    };
    this.messages.push(userMessage);

    const messageToApi = this.currentMessage.trim() || 'Please review the attached files.';
    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;
    this.scrollToBottom();

    (await this.chatApiService.sendMessage(messageToApi)).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        const agentText = response?.message || 'No response from agent.';
        this.messages.push({
          text: agentText,
          sender: 'agent',
          formattedText: this.formatText(agentText),
        });
        this.scrollToBottom();
      },
      error: (error: any) => {
        this.isLoading = false;
        const errorMessage = error?.message || 'Sorry, something went wrong.';
        this.messages.push({
          text: errorMessage,
          sender: 'agent',
          formattedText: this.formatText(errorMessage),
        });
        this.scrollToBottom();
      },
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.chatBody?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }
}
